import { useState } from "react";

import { useQuery } from "@apollo/client";
import { Select } from "antd";
import { clsx } from "clsx";
import find from "lodash/find";

import { RVFormFloatingLabelWrap, RVLink } from "@regal-voice/ui";
import { SegmentDisplay } from "Components/elements/ObjectDisplays/SegmentDisplay";
import { renderErrorMessage } from "Services/LoggingService";
import { getSegments } from "Services/marketing-api/segments/queries";
import { Segment } from "Types/Segment";

import styles from "./SegmentSelect.module.scss";

type SegmentSelectProps = {
    onChange?: (segmentId: string, segmentName: string) => void;
    className?: string;
    formValues?: Record<string, any>;
    segmentsBasePath: string;
};

/**
 *
 * Shared Segment Select Form Item, ideally can jsut plop this puppy down wherever you need a segment select
 * without much effort
 *
 * @param onChange - Optional onChange for a parent to pass down
 * @param className - Any additional classes to be passed down from a parent
 * @param formValues - An object containing the most up to date form values. This is sort of a
 *                     journey builder paradigm, that im not sure is the best but that ive
 *                     accidentally made the default way of handling form changes.
 * @returns JSX.Element - A Segment select dropdown with the latest segments
 */
export function SegmentSelect({
    segmentsBasePath,
    onChange,
    className = "",
    formValues,
}: SegmentSelectProps): JSX.Element {
    const [selectedSegmentId, setSelectedSegmentId] = useState<string>(formValues?.segmentId);
    const segmentIsSelected = !!selectedSegmentId;

    const { data } = useQuery(getSegments, {
        variables: {
            queryParams: {},
        },
        fetchPolicy: "cache-and-network",
        errorPolicy: "all",
        onError(e: any) {
            renderErrorMessage({ content: `Error fetching segments: ${e.message}`, error: e });
        },
    });

    function onSegmentChange(segmentId: string) {
        const segmentName = find(data?.getSegments?.items, (s) => s.id === segmentId)?.name;
        onChange?.(segmentId, segmentName);
        setSelectedSegmentId(segmentId);
    }

    return (
        // TODO: This import is from the journeys page, cristi has a big PR that includes a shared component
        // which is essentially this, once thats merged I'll consolidate the 2 and update this use the
        // shared component
        <RVFormFloatingLabelWrap
            label="Segment"
            name="segmentId"
            containerClassName={className}
            className={clsx(styles.selectContainer)}
            rules={[
                {
                    required: true,
                    message: "Select a Segment",
                },
            ]}
            extra={
                !segmentIsSelected && (
                    <RVLink
                        data-testid="create-segment-cta-testid"
                        to={`${segmentsBasePath}/new`}
                        text="Create Segment"
                    />
                )
            }
        >
            <Select onChange={onSegmentChange} value={selectedSegmentId}>
                {data?.getSegments?.items?.map((segment: Segment) => (
                    <Select.Option key={`segment-${segment.id}`} value={segment.id}>
                        <SegmentDisplay segment={segment} />
                    </Select.Option>
                ))}
            </Select>
        </RVFormFloatingLabelWrap>
    );
}
